import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { getDisplayTitle } from '@/lib/displayTitle';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    RefreshCw, MapPin, MessageSquare, Send, Wifi, WifiOff, Clipboard,
    ChevronRight, Loader2, User, Navigation,
} from 'lucide-react';

const ACCENT = '#7C6BEF';
// Fix default icon path issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom colored pin
function pinIcon(color, initial, isOnline) {
    return L.divIcon({
        className: 'custom-pin',
        html: `<div style="position:relative;transform:translate(-50%,-100%)">
            <div style="width:36px;height:36px;background:${color};border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 12px rgba(0,0,0,0.35);border:3px solid #fff;display:flex;align-items:center;justify-content:center;">
                <span style="transform:rotate(45deg);color:white;font-weight:900;font-size:14px;">${initial}</span>
            </div>
            <div style="position:absolute;top:-4px;right:-4px;width:12px;height:12px;background:${isOnline ? '#10B981' : '#9CA3AF'};border-radius:50%;border:2px solid #fff;"></div>
        </div>`,
        iconSize: [36, 44], iconAnchor: [18, 44], popupAnchor: [0, -40],
    });
}

function isOnline(iso) {
    if (!iso) return false;
    return (Date.now() - new Date(iso).getTime()) < 15 * 60 * 1000;
}

function timeAgo(iso) {
    if (!iso) return '—';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (min < 1) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    return `hace ${d} d`;
}

function FitBoundsOnLoad({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points.length === 0) return;
        if (points.length === 1) {
            map.setView([points[0].lat, points[0].lng], 13);
        } else {
            const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
            map.fitBounds(bounds, { padding: [40, 40] });
        }
    }, [map, points.length]); // eslint-disable-line
    return null;
}

export default function AdminTeamMap() {
    const [data, setData] = useState({ inspectors: [] });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = () => {
        setRefreshing(true);
        return api.get('/admin/team-map')
            .then((r) => {
                setData(r.data);
                if (!selected && r.data.inspectors[0]) setSelected(r.data.inspectors[0].id);
            })
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar')))
            .finally(() => { setLoading(false); setRefreshing(false); });
    };

    useEffect(() => {
        load();
        const iv = setInterval(load, 30000); // Auto-refresh 30s
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const withLocation = useMemo(() => data.inspectors.filter((i) => i.latitude && i.longitude), [data]);
    const selectedInspector = data.inspectors.find((i) => i.id === selected);
    const [distintivos, setDistintivos] = useState([]);
    useEffect(() => { api.get('/public/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => setDistintivos([])); }, []);
    const distLookup = useMemo(() => Object.fromEntries(distintivos.map((d) => [d.id, d])), [distintivos]);

    const center = withLocation[0]
        ? [withLocation[0].latitude, withLocation[0].longitude]
        : [21.16, -86.85]; // Cancún default

    return (
        <AdminLayout title="Mapa equipo">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: ACCENT }}>Operaciones en Campo</div>
                    <h1 className="font-display text-3xl font-black tracking-tight">Mapa del equipo auditor</h1>
                    <div className="text-xs text-gray-500 mt-1">
                        {withLocation.length} de {data.inspectors.length} auditores con ubicación reciente.
                    </div>
                </div>
                <button onClick={load} disabled={refreshing} data-testid="refresh-map"
                    className="inline-flex items-center gap-2 px-4 py-2 border font-bold text-sm hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }}>
                    {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Actualizar
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Map */}
                <div className="lg:col-span-2" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', height: 600 }} data-testid="team-map">
                    {loading ? (
                        <div className="h-full flex items-center justify-center text-gray-500">Cargando mapa...</div>
                    ) : (
                        <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }}>
                            <FitBoundsOnLoad points={withLocation.map((i) => ({ lat: i.latitude, lng: i.longitude }))} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OpenStreetMap'
                            />
                            {withLocation.map((i) => {
                                const online = isOnline(i.location_updated_at);
                                const initial = (i.name || 'I').charAt(0).toUpperCase();
                                return (
                                    <Marker key={i.id} position={[i.latitude, i.longitude]}
                                        icon={pinIcon(online ? '#10B981' : ACCENT, initial, online)}
                                        eventHandlers={{ click: () => setSelected(i.id) }}>
                                        <Popup>
                                            <div className="font-bold text-sm">{i.name}</div>
                                            <div className="text-xs text-gray-500">{i.email}</div>
                                            <div className="text-[11px] mt-1">
                                                {online ? '🟢 En línea' : '⚪ Offline'} · {timeAgo(i.location_updated_at)}
                                            </div>
                                            {i.current_assignment?.[0] && (
                                                <div className="text-[11px] mt-1 pt-1 border-t">
                                                    📋 {getDisplayTitle(i.current_assignment[0], distLookup).title} — {i.current_assignment[0].client_name}
                                                </div>
                                            )}
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MapContainer>
                    )}
                </div>

                {/* Right panel */}
                <div className="space-y-4">
                    {/* Inspector list */}
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                            <h3 className="font-display font-bold text-sm">Auditores</h3>
                        </div>
                        <div className="max-h-[320px] overflow-y-auto divide-y" style={{ borderColor: 'var(--border)' }}>
                            {data.inspectors.map((i) => {
                                const online = isOnline(i.location_updated_at);
                                return (
                                    <button key={i.id} onClick={() => setSelected(i.id)}
                                        data-testid={`inspector-row-${i.id}`}
                                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-black/5 dark:hover:bg-white/5"
                                        style={{ background: selected === i.id ? 'rgba(124,107,239,0.10)' : 'transparent' }}>
                                        <div className="relative shrink-0">
                                            {i.photo_url ? (
                                                <img src={i.photo_url} alt="" className="w-9 h-9 object-cover rounded-full" />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ background: '#00875A' }}>
                                                    {(i.name || 'I').charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900"
                                                style={{ background: online ? '#10B981' : '#9CA3AF' }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm truncate">{i.name}</div>
                                            <div className="text-[11px] text-gray-500 truncate">
                                                {i.location_updated_at ? timeAgo(i.location_updated_at) : 'Sin reporte'}
                                            </div>
                                        </div>
                                        {online ? <Wifi className="w-3.5 h-3.5 text-emerald-500" /> : <WifiOff className="w-3.5 h-3.5 text-gray-400" />}
                                    </button>
                                );
                            })}
                            {data.inspectors.length === 0 && (
                                <div className="px-4 py-6 text-center text-xs text-gray-500">Sin auditores activos</div>
                            )}
                        </div>
                    </div>

                    {/* Selected inspector detail */}
                    {selectedInspector && <InspectorPanel inspector={selectedInspector} distLookup={distLookup} />}
                </div>
            </div>
        </AdminLayout>
    );
}

function InspectorPanel({ inspector, distLookup }) {
    const online = isOnline(inspector.location_updated_at);
    return (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="inspector-panel">
            <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="font-display font-black text-base truncate">{inspector.name}</div>
                        <div className="text-xs text-gray-500 truncate">{inspector.email}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                        style={{ background: online ? 'rgba(16,185,129,0.15)' : 'rgba(156,163,175,0.15)', color: online ? '#10B981' : '#9CA3AF' }}>
                        {online ? 'En línea' : 'Offline'}
                    </span>
                </div>
            </div>
            <div className="p-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                    <StatBox label="Última ubicación" value={inspector.latitude ? `${inspector.latitude.toFixed(4)}, ${inspector.longitude.toFixed(4)}` : '—'} />
                    <StatBox label="Auditoría actual" value={inspector.current_assignment?.[0] ? getDisplayTitle(inspector.current_assignment[0], distLookup).title : 'Sin asignación'} />
                </div>
                {inspector.current_assignment?.[0] && (
                    <div className="border-l-2 pl-3 text-xs" style={{ borderColor: ACCENT }}>
                        <div className="font-bold">{inspector.current_assignment[0].client_name}</div>
                        <div className="text-gray-500">{inspector.current_assignment[0].client_address || 'Sin dirección'}</div>
                    </div>
                )}
                {inspector.latitude && inspector.longitude && (
                    <a href={`https://www.google.com/maps?q=${inspector.latitude},${inspector.longitude}`}
                        target="_blank" rel="noreferrer"
                        data-testid="open-inspector-in-maps"
                        className="block text-xs font-bold hover:underline" style={{ color: ACCENT }}>
                        Abrir en Google Maps ↗
                    </a>
                )}
                {inspector.current_assignment?.[0] && (
                    <RouteToClientButton inspector={inspector} assignment={inspector.current_assignment[0]} />
                )}
            </div>
            <ChatPanel inspector={inspector} />
        </div>
    );
}

function StatBox({ label, value }) {
    return (
        <div className="border p-2" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 mb-0.5">{label}</div>
            <div className="text-xs font-bold truncate">{value}</div>
        </div>
    );
}

function RouteToClientButton({ inspector, assignment }) {
    const hasClient = assignment.client_lat && assignment.client_lng;
    const hasClientAddress = assignment.client_address;
    const hasInspector = inspector.latitude && inspector.longitude;
    if (!hasClient && !hasClientAddress) return null;
    // Google Maps directions: prefer coordinates; fallback to address
    const dest = hasClient
        ? `${assignment.client_lat},${assignment.client_lng}`
        : encodeURIComponent(assignment.client_address);
    const origin = hasInspector ? `${inspector.latitude},${inspector.longitude}` : '';
    const url = origin
        ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
    return (
        <a href={url} target="_blank" rel="noreferrer"
            data-testid="route-to-client"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90"
            style={{ background: ACCENT }}>
            <Navigation className="w-3.5 h-3.5" />
            Enrutar al cliente
        </a>
    );
}

function ChatPanel({ inspector }) {
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);

    const load = () => api.get('/messages', { params: { with_user: inspector.id } })
        .then((r) => setMessages(r.data || []))
        .catch(() => {});

    useEffect(() => {
        load();
        const iv = setInterval(load, 15000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inspector.id]);

    const send = async (e) => {
        e.preventDefault();
        if (!draft.trim()) return;
        setSending(true);
        try {
            await api.post('/admin/messages', { to_user_id: inspector.id, body: draft.trim() });
            setDraft('');
            await load();
        } catch (err) { toast.error(apiErrorMessage(err, 'Error al enviar')); }
        finally { setSending(false); }
    };

    return (
        <div className="border-t" style={{ borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 flex items-center gap-2 text-sm font-bold">
                <MessageSquare className="w-4 h-4" style={{ color: ACCENT }} /> Mensajes
            </div>
            <div className="max-h-56 overflow-y-auto px-4 pb-3 space-y-2" data-testid="chat-thread">
                {messages.length === 0 ? (
                    <div className="text-xs text-gray-400 italic text-center py-4">Sin mensajes aún.</div>
                ) : (
                    messages.map((m) => (
                        <div key={m.id} className={`text-xs p-2 max-w-[85%] ${m.from_role === 'admin' ? 'ml-auto bg-[#7C6BEF]/15' : 'bg-gray-100 dark:bg-white/5'}`}>
                            {m.body}
                            <div className="text-[9px] text-gray-400 mt-1">{timeAgo(m.sent_at)}</div>
                        </div>
                    ))
                )}
            </div>
            <form onSubmit={send} className="p-3 border-t flex gap-2" style={{ borderColor: 'var(--border)' }}>
                <input value={draft} onChange={(e) => setDraft(e.target.value)}
                    placeholder="Escribe un mensaje..." data-testid="chat-input"
                    className="flex-1 px-3 py-2 border outline-none text-sm"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                <button type="submit" disabled={sending || !draft.trim()} data-testid="chat-send"
                    className="px-3 py-2 text-white font-bold text-sm inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ background: ACCENT }}>
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </form>
        </div>
    );
}
